/**
 * TutoringAgreementModal - role-specific signing modal for independent tutoring.
 *
 * Fetches the current agreement document for the signer's role, renders the reusable ConsentForm
 * (scroll-to-end + typed-name e-signature), enforces that ALL required authorizations are granted
 * (no partial consent for independent tutoring), and records the signature. On success it reports
 * whether the relationship is now fully active so the caller can refresh.
 */
import { useState, useEffect } from 'react'
import { X, Loader2, AlertCircle, Ban } from 'lucide-react'
import toast from 'react-hot-toast'
import ConsentForm from '../consent/ConsentForm'
import tutoringService, {
  TUTORING_SLUG_BY_ROLE,
  TUTORING_ROLE_LABELS
} from '../../services/tutoringService'

const TutoringAgreementModal = ({
  isOpen,
  relationshipId,
  signerRole,
  studentName,
  onClose,
  onSigned
}) => {
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState(false)
  // The agreement was cancelled/declined (or already activated) before this party signed — no form.
  const [inactive, setInactive] = useState(false)

  useEffect(() => {
    if (!isOpen || !signerRole) return

    let cancelled = false
    const slug = TUTORING_SLUG_BY_ROLE[signerRole]

    const load = async () => {
      setLoading(true)
      setError('')
      setDocument(null)
      setInactive(false)
      try {
        // Gate on readiness first: if the relationship is no longer awaiting signatures, show the
        // "no longer active" state instead of a signable form.
        if (relationshipId) {
          const readiness = await tutoringService.getReadiness(relationshipId)
          if (cancelled) return
          if (readiness && readiness.signable === false) {
            setInactive(true)
            return
          }
        }
        const doc = await tutoringService.getDocument(slug)
        if (!cancelled) setDocument(doc)
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load the agreement document')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, signerRole, relationshipId])

  const handleSubmit = async ({ consentTypes, signatureName }) => {
    // Independent tutoring requires every listed authorization (no partial consent).
    const required = document?.consentTypes || []
    const grantedAll = required.every((t) => consentTypes.includes(t))
    if (!grantedAll) {
      toast.error('Please authorize all listed items to continue.')
      return
    }

    setProcessing(true)
    try {
      const result = await tutoringService.sign(relationshipId, {
        signerRole,
        consentTypes,
        signatureName
      })
      toast.success(
        result.activated
          ? 'Signed — tutoring is now active!'
          : 'Agreement signed. Tutoring will begin once the remaining agreements are signed.'
      )
      onSigned?.(result)
      onClose?.()
    } catch (err) {
      // Race: the relationship was cancelled/declined between opening and signing — swap to the
      // inactive state rather than letting the user keep trying.
      if (err.code === 'RELATIONSHIP_NOT_SIGNABLE') {
        setInactive(true)
        onSigned?.({ activated: false, inactive: true })
      }
      toast.error(err.message || 'Failed to record signature')
    } finally {
      setProcessing(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {TUTORING_ROLE_LABELS[signerRole] || 'Tutoring agreement'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading agreement…
            </div>
          )}

          {!loading && error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && inactive && (
            <div className="py-8 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <Ban className="w-7 h-7 text-gray-500 dark:text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                This agreement is no longer active
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                The tutoring request was cancelled or declined, so it can no longer be signed. If
                you still want to set up tutoring, please start a new request.
              </p>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          )}

          {!loading && !error && !inactive && document && (
            <ConsentForm
              document={document}
              studentName={studentName}
              onSubmit={handleSubmit}
              processing={processing}
              submitLabel="Sign agreement"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default TutoringAgreementModal
